import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";
import { Calendar } from "./ui/calendar";
import { ptBR } from "date-fns/locale";
import { differenceInCalendarDays, format } from "date-fns";
import {
  CABINS,
  PET_FEE,
  PAYMENT_OPTIONS,
  CONTACT,
} from "../data/cabins";
import {
  CalendarDays,
  CheckCircle2,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatBRL = (v) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const maskCPF = (v) =>
  v
    .replace(/\D/g, "")
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");

const maskPhone = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 10) {
    return d
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  }
  return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
};

export const BookingForm = ({ selectedCabinId, setSelectedCabinId }) => {
  const cabin = useMemo(
    () => CABINS.find((c) => c.id === selectedCabinId) || CABINS[0],
    [selectedCabinId]
  );

  const [range, setRange] = useState({ from: undefined, to: undefined });

  const [form, setForm] = useState({
    full_name: "",
    birth_date: "",
    cpf: "",
    email: "",
    phone: "",
    companion_name: "",
    num_guests: "2",
    has_pet: "no",
    payment_method: "pix",
    special_requests: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const nights =
    range?.from && range?.to
      ? Math.max(0, differenceInCalendarDays(range.to, range.from))
      : 0;
  const subtotal = nights * cabin.daily_rate;
  const petFee = form.has_pet === "yes" ? PET_FEE : 0;
  const total = subtotal + petFee;

  const installments =
    form.payment_method === "pix"
      ? 1
      : Number(form.payment_method.replace("credit_", "").replace("x", "")) ||
        1;

  const update = (k) => (e) => {
    const value =
      k === "cpf"
        ? maskCPF(e.target.value)
        : k === "phone"
        ? maskPhone(e.target.value)
        : e.target.value;
    setForm((p) => ({ ...p, [k]: value }));
  };

  const validate = () => {
    if (!range?.from || !range?.to)
      return "Selecione a data de check-in e check-out no calendário.";
    if (nights < 1) return "A reserva precisa de pelo menos 1 diária.";
    if (!form.full_name.trim()) return "Informe seu nome completo.";
    if (!form.birth_date) return "Informe sua data de nascimento.";
    if (form.cpf.replace(/\D/g, "").length !== 11) return "CPF inválido.";
    if (!/.+@.+\..+/.test(form.email)) return "E-mail inválido.";
    if (form.phone.replace(/\D/g, "").length < 10)
      return "Telefone/WhatsApp inválido.";
    const ng = Number(form.num_guests);
    if (!ng || ng < 1) return "Informe o número de hóspedes.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    const err = validate();
    if (err) {
      setErrorMsg(err);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        cabin_id: cabin.id,
        cabin_name: cabin.name,
        daily_rate: cabin.daily_rate,
        check_in: format(range.from, "yyyy-MM-dd"),
        check_out: format(range.to, "yyyy-MM-dd"),
        nights,
        full_name: form.full_name.trim(),
        birth_date: form.birth_date,
        cpf: form.cpf,
        email: form.email.trim(),
        phone: form.phone,
        companion_name: form.companion_name.trim(),
        num_guests: Number(form.num_guests),
        has_pet: form.has_pet === "yes",
        payment_method: form.payment_method,
        installments,
        special_requests: form.special_requests.trim(),
        pet_fee: petFee,
        total,
      };
      await axios.post(`${API}/reservations`, payload);
      setSuccess(true);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Não foi possível enviar a reserva. Tente novamente.";
      setErrorMsg(typeof msg === "string" ? msg : "Erro ao enviar reserva.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    setSuccess(false);
    setRange({ from: undefined, to: undefined });
    setForm({
      full_name: "",
      birth_date: "",
      cpf: "",
      email: "",
      phone: "",
      companion_name: "",
      num_guests: "2",
      has_pet: "no",
      payment_method: "pix",
      special_requests: "",
    });
  };

  // Allow today and any future date
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const disabledMatcher = { before: today };

  useEffect(() => {
    // Clear error when changing selections
    setErrorMsg("");
  }, [range, form, selectedCabinId]);

  return (
    <section
      id="reserva"
      data-testid="booking-section"
      className="py-20 md:py-28 bg-[#e8e5d9]"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl mb-12">
          <p className="text-sm uppercase tracking-[0.2em] text-[#b55a30] font-bold mb-3">
            Reserva
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#1a3622] leading-tight">
            Escolha o chalé e as datas.
          </h2>
          <p className="mt-4 text-[#4a6b53] leading-relaxed">
            Sem pagamento online. Sua solicitação chega direto no nosso WhatsApp
            e confirmamos em poucos minutos.
          </p>
        </div>

        {/* Cabin selector */}
        <div className="flex flex-wrap gap-3 mb-8" data-testid="cabin-selector">
          {CABINS.map((c) => (
            <button
              key={c.id}
              onClick={() => setSelectedCabinId(c.id)}
              data-testid={`cabin-chip-${c.id}`}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold border transition-all ${
                c.id === cabin.id
                  ? "bg-[#1a3622] text-[#f7f5f0] border-[#1a3622]"
                  : "bg-transparent text-[#1a3622] border-[#1a3622]/30 hover:border-[#1a3622]"
              }`}
            >
              {c.name} — {formatBRL(c.daily_rate)}
            </button>
          ))}
        </div>

        <form
          onSubmit={submit}
          noValidate
          className="grid lg:grid-cols-12 gap-8"
          data-testid="booking-form"
        >
          {/* Calendar (above the form fields) */}
          <div className="lg:col-span-7 bg-[#f7f5f0] rounded-2xl border border-[#d1cfc3] p-6 sm:p-8 card-soft">
            <div className="flex items-center gap-2 mb-5">
              <CalendarDays className="h-5 w-5 text-[#1a3622]" />
              <h3 className="text-lg font-bold text-[#1a3622]">
                Selecione check-in e check-out
              </h3>
            </div>
            <div className="overflow-x-auto">
              <Calendar
                mode="range"
                selected={range}
                onSelect={(r) => setRange(r || { from: undefined, to: undefined })}
                numberOfMonths={2}
                disabled={disabledMatcher}
                locale={ptBR}
                showOutsideDays
                data-testid="booking-calendar"
              />
            </div>

            <div className="mt-6 grid sm:grid-cols-3 gap-4 text-sm">
              <div className="rounded-xl bg-[#e8e5d9] p-4">
                <p className="uppercase text-[10px] tracking-[0.2em] text-[#4a6b53] font-bold">
                  Check-in
                </p>
                <p className="mt-1 font-bold text-[#1a3622]" data-testid="ci-label">
                  {range?.from
                    ? format(range.from, "dd/MM/yyyy", { locale: ptBR })
                    : "—"}
                </p>
              </div>
              <div className="rounded-xl bg-[#e8e5d9] p-4">
                <p className="uppercase text-[10px] tracking-[0.2em] text-[#4a6b53] font-bold">
                  Check-out
                </p>
                <p className="mt-1 font-bold text-[#1a3622]" data-testid="co-label">
                  {range?.to
                    ? format(range.to, "dd/MM/yyyy", { locale: ptBR })
                    : "—"}
                </p>
              </div>
              <div className="rounded-xl bg-[#1a3622] text-[#f7f5f0] p-4">
                <p className="uppercase text-[10px] tracking-[0.2em] text-[#f7f5f0]/70 font-bold">
                  Diárias
                </p>
                <p
                  className="mt-1 font-black text-xl"
                  data-testid="nights-label"
                >
                  {nights}
                </p>
              </div>
            </div>
          </div>

          {/* Price summary */}
          <aside className="lg:col-span-5 bg-[#1a3622] text-[#f7f5f0] rounded-2xl p-6 sm:p-8 card-soft sticky top-24 self-start">
            <p className="text-xs uppercase tracking-[0.2em] text-[#f7f5f0]/70 font-bold mb-2">
              Resumo
            </p>
            <h3 className="text-2xl font-black">{cabin.name}</h3>
            <p className="text-sm text-[#f7f5f0]/75 mt-1">{cabin.tagline}</p>

            <div className="mt-6 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-[#f7f5f0]/80">
                  {formatBRL(cabin.daily_rate)} × {nights} diária(s)
                </span>
                <span className="font-bold" data-testid="subtotal">
                  {formatBRL(subtotal)}
                </span>
              </div>
              {petFee > 0 && (
                <div className="flex justify-between">
                  <span className="text-[#f7f5f0]/80">Taxa pet</span>
                  <span className="font-bold" data-testid="pet-fee">
                    {formatBRL(petFee)}
                  </span>
                </div>
              )}
              <div className="border-t border-white/15 pt-3 flex justify-between items-baseline">
                <span className="text-[#f7f5f0]/85">Total</span>
                <span
                  className="text-2xl font-black text-[#f7f5f0]"
                  data-testid="total-amount"
                >
                  {formatBRL(total)}
                </span>
              </div>
              {form.payment_method !== "pix" && installments > 1 && (
                <p className="text-xs text-[#f7f5f0]/70">
                  Em {installments}x de {formatBRL(total / installments)} sem
                  juros.
                </p>
              )}
            </div>
          </aside>

          {/* Form fields */}
          <div className="lg:col-span-12 bg-[#f7f5f0] rounded-2xl border border-[#d1cfc3] p-6 sm:p-8 card-soft">
            <h3 className="text-lg font-bold text-[#1a3622] mb-6">
              Dados do hóspede
            </h3>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field
                label="Nome completo *"
                testId="input-full-name"
                value={form.full_name}
                onChange={update("full_name")}
                placeholder="Como deve constar na reserva"
              />
              <Field
                label="Data de nascimento *"
                testId="input-birth-date"
                type="date"
                value={form.birth_date}
                onChange={update("birth_date")}
              />
              <Field
                label="CPF *"
                testId="input-cpf"
                value={form.cpf}
                onChange={update("cpf")}
                placeholder="000.000.000-00"
                inputMode="numeric"
              />
              <Field
                label="E-mail *"
                testId="input-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="seu@email.com"
              />
              <Field
                label="Telefone (WhatsApp) *"
                testId="input-phone"
                value={form.phone}
                onChange={update("phone")}
                placeholder="(11) 98765-4321"
                inputMode="tel"
              />
              <Field
                label="Nome do acompanhante"
                testId="input-companion"
                value={form.companion_name}
                onChange={update("companion_name")}
                placeholder="Opcional"
              />
              <Field
                label="Número de hóspedes *"
                testId="input-guests"
                type="number"
                min={1}
                max={4}
                value={form.num_guests}
                onChange={update("num_guests")}
              />

              <SelectField
                label="Levará pet? *"
                testId="input-pet"
                value={form.has_pet}
                onChange={update("has_pet")}
                options={[
                  { value: "no", label: "Não" },
                  { value: "yes", label: "Sim (taxa R$ 200)" },
                ]}
              />

              <SelectField
                label="Forma de pagamento *"
                testId="input-payment"
                value={form.payment_method}
                onChange={update("payment_method")}
                options={PAYMENT_OPTIONS}
                fullSpan
              />

              <div className="sm:col-span-2">
                <label className="block text-sm font-bold text-[#1a3622] mb-1.5">
                  Alguma solicitação especial?
                </label>
                <textarea
                  data-testid="input-special"
                  value={form.special_requests}
                  onChange={update("special_requests")}
                  rows={3}
                  placeholder="Comemoração, restrição alimentar, horário de chegada..."
                  className="w-full rounded-lg border border-[#d1cfc3] bg-white px-4 py-3 text-sm text-[#1a3622] focus:outline-none focus:ring-2 focus:ring-[#1a3622]/30"
                />
              </div>
            </div>

            {errorMsg && (
              <div
                role="alert"
                data-testid="form-error"
                className="mt-5 flex items-start gap-3 rounded-lg bg-[#fbeae1] border border-[#b55a30]/30 text-[#7a3a1f] px-4 py-3 text-sm"
              >
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}

            <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-xs text-[#4a6b53] max-w-md">
                Ao enviar, sua solicitação chegará no WhatsApp{" "}
                <strong>{CONTACT.phone}</strong>. Não há cobrança online.
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-booking-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1a3622] text-[#f7f5f0] px-8 py-3.5 text-sm font-semibold hover:bg-[#2d5638] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Enviando...
                  </>
                ) : (
                  <>Solicitar reserva</>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      <Dialog open={success} onOpenChange={(o) => !o && resetAll()}>
        <DialogContent
          className="bg-[#f7f5f0] border-[#d1cfc3]"
          data-testid="success-dialog"
        >
          <DialogHeader>
            <div className="mx-auto h-14 w-14 rounded-full bg-[#1a3622] text-[#f7f5f0] flex items-center justify-center mb-3">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <DialogTitle className="text-center text-2xl font-black text-[#1a3622]">
              Sua reserva foi concluída com Sucesso!
            </DialogTitle>
            <DialogDescription className="text-center text-[#4a6b53]">
              Recebemos sua solicitação e enviamos os dados para o nosso
              WhatsApp. Entraremos em contato em breve para confirmação.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-2">
            <button
              onClick={resetAll}
              data-testid="success-close"
              className="rounded-full bg-[#1a3622] text-[#f7f5f0] px-6 py-2.5 text-sm font-semibold hover:bg-[#2d5638] transition-colors"
            >
              Fechar
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

const Field = ({ label, testId, type = "text", ...rest }) => (
  <div>
    <label className="block text-sm font-bold text-[#1a3622] mb-1.5">
      {label}
    </label>
    <input
      data-testid={testId}
      type={type}
      {...rest}
      className="w-full rounded-lg border border-[#d1cfc3] bg-white px-4 py-3 text-sm text-[#1a3622] focus:outline-none focus:ring-2 focus:ring-[#1a3622]/30"
    />
  </div>
);

const SelectField = ({ label, testId, options, fullSpan, ...rest }) => (
  <div className={fullSpan ? "sm:col-span-2" : ""}>
    <label className="block text-sm font-bold text-[#1a3622] mb-1.5">
      {label}
    </label>
    <select
      data-testid={testId}
      {...rest}
      className="w-full rounded-lg border border-[#d1cfc3] bg-white px-4 py-3 text-sm text-[#1a3622] focus:outline-none focus:ring-2 focus:ring-[#1a3622]/30"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </div>
);
